import React, { useState, useEffect, useMemo } from 'react';
import Shell from './tuteur/Shell';
import { slotLabel } from './FormsDrill';
import Lecon from './Lecon';

// ── Tableaux de formes (page PERSONNELLE, propriétaire uniquement) ──────────
// Accès via ?tableaux. Données : /api/formes/tables (401/403 → redirection).
//
// Deux tableaux, aucun texte pédagogique inventé : la conjugaison des verbes les
// plus fréquents, et un modèle de déclinaison par famille. Le contenu vient
// entièrement de la table `formes`, elle-même produite par pymorphy3.
//
// Le seul parti pris : chaque modèle est illustré par un mot que le lecteur
// travaille déjà quand il en existe un. Décliner un mot qu'on connaît apprend la
// terminaison ; décliner un mot inconnu apprend deux choses à la fois, donc rien.
//
// Limite assumée : pas d'accent tonique. pymorphy ne le donne pas, et l'inventer
// serait pire que de l'omettre. L'audio des formes viendra le combler.

const TOKEN_KEY = 'motrusse_token';
const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";

const CASE_ORDER = ['nomn', 'gent', 'datv', 'accs', 'ablt', 'loct'];
const VERB_ROWS = [
  ['inf', 'infinitif'],
  ['pres.1sg', 'я'], ['pres.2sg', 'ты'], ['pres.3sg', 'он / она'],
  ['pres.1pl', 'мы'], ['pres.2pl', 'вы'], ['pres.3pl', 'они'],
  ['past.m', 'passé masculin'], ['past.f', 'passé féminin'],
  ['past.n', 'passé neutre'], ['past.pl', 'passé pluriel'],
  ['impr.sg', 'impératif tu'], ['impr.pl', 'impératif vous'],
];

const MODELE_FR = {
  'm.dur': 'Masculin dur (consonne)',
  'm.mou': 'Masculin mou (-ь)',
  'm.j': 'Masculin en -й',
  'm.a': 'Masculin en -а / -я',
  'f.a': 'Féminin en -а',
  'f.ja': 'Féminin en -я',
  'f.mou': 'Féminin en -ь',
  'n.o': 'Neutre en -о',
  'n.e': 'Neutre en -е',
  'n.mja': 'Neutre en -мя',
  'adj.dur': 'Adjectif dur',
  'adj.mou': 'Adjectif mou',
};

const Ru = ({ children, dim }) => (
  <span lang="ru" style={{ fontFamily: SERIF }} className={dim ? 'text-slate-300' : 'text-blue-300'}>
    {children}
  </span>
);

// Haut-parleur discret, à côté d'une forme. Il n'apparaît que si le mp3 existe :
// l'audio ne couvre que les tableaux (50 verbes et modèles de déclinaison), pas
// les 67 000 formes de la base.
//
// C'est lui qui porte l'ACCENT TONIQUE, absent de la source écrite. о́кна et окна́
// s'écrivent pareil et ne se disent pas pareil : sans le son, un tableau enseigne
// une terminaison juste et une prononciation inventée.
const Say = ({ file }) => {
  if (!file) return null;
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        try { new Audio(`/static/${file}`).play().catch(() => {}); } catch {}
      }}
      title="Écouter"
      aria-label="Écouter"
      className="ml-2 align-middle inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-700 hover:bg-blue-600 transition-colors shrink-0"
    >
      <svg viewBox="0 0 24 24" className="w-3 h-3 fill-slate-300" aria-hidden="true">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
      </svg>
    </button>
  );
};

// Un verbe : en-tête cliquable, tableau déplié à la demande. Tout déplier d'un
// coup donnerait cinquante tableaux illisibles ; l'intérêt est d'aller chercher
// un verbe précis.
const VerbCard = ({ v, open, onToggle }) => {
  // Un verbe qui a un présent est imperfectif, sinon ses formes personnelles
  // sont un futur. On adapte donc juste l'intitulé de la colonne.
  const isPerf = v.aspect === 'perf';
  const rows = VERB_ROWS.filter(([slot]) => v.formes[slot] || v.formes[slot.replace('pres.', 'futr.')]);
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-baseline gap-3 px-4 py-3 hover:bg-slate-700/50 transition-colors text-left">
        <Ru>{v.lemma}</Ru>
        <span className="text-sm text-slate-400 truncate">{v.sens}</span>
        <span className="ml-auto shrink-0 text-[11px] text-slate-500">
          {isPerf ? 'perfectif' : 'imperfectif'}
          {v.connu && ' · connu'}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-700/60">
          <p className="text-[11px] text-slate-500 mb-2">
            {isPerf
              ? 'Perfectif : pas de présent, les formes personnelles sont un futur.'
              : 'Imperfectif : présent, et futur composé avec буду.'}
          </p>
          <table className="w-full text-sm">
            <tbody>
              {rows.map(([slot, label]) => {
                const key = v.formes[slot] ? slot : slot.replace('pres.', 'futr.');
                return (
                  <tr key={slot} className="border-t border-slate-700/40 first:border-0">
                    <td className="py-1.5 pr-3 text-slate-500 text-xs w-32 align-baseline">
                      {/* L'étiquette suit la forme AFFICHÉE, pas l'aspect du verbe.
                          быть est imperfectif mais son futur est simple (буду) :
                          la ligne « я » montrait donc un futur sans le dire. */}
                      {key.startsWith('futr.') ? `${label} (futur)` : label}
                    </td>
                    <td className="py-1.5 align-baseline">
                      <Ru>{v.formes[key]}</Ru>
                      <Say file={v.audio?.[key]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Un modèle de déclinaison : les six cas en deux colonnes (singulier, pluriel).
// Les adjectifs n'ont pas de singulier/pluriel dans ce découpage mais un genre :
// leurs cases sont `.m/.f/.n/.pl`, d'où les deux jeux de colonnes possibles.
const ModelCard = ({ m }) => {
  const isAdj = m.modele.startsWith('adj.');
  const cols = isAdj
    ? [['m', 'masculin'], ['f', 'féminin'], ['n', 'neutre'], ['pl', 'pluriel']]
    : [['sg', 'singulier'], ['pl', 'pluriel']];
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col gap-3">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h3 className="font-semibold text-white">{MODELE_FR[m.modele] || m.modele}</h3>
        <span className="text-xs text-slate-500">{m.n} mots du dictionnaire</span>
      </div>
      <p className="text-sm text-slate-400">
        <Ru>{m.lemma}</Ru> · {m.sens}
        {m.connu && <span className="text-slate-500"> · un mot que tu travailles</span>}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[20rem]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-500">
              <th className="text-left font-medium pb-1">cas</th>
              {cols.map(([, label]) => <th key={label} className="text-left font-medium pb-1">{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {CASE_ORDER.map((c) => (
              <tr key={c} className="border-t border-slate-700/40">
                <td className="py-1.5 pr-3 text-slate-500 text-xs align-baseline">{slotLabel(`${c}.sg`).split(' ')[0]}</td>
                {cols.map(([suffix]) => (
                  <td key={suffix} className="py-1.5 pr-3 align-baseline whitespace-nowrap">
                    {m.formes[`${c}.${suffix}`] ? (
                      <>
                        <Ru dim>{m.formes[`${c}.${suffix}`]}</Ru>
                        <Say file={m.audio?.[`${c}.${suffix}`]} />
                      </>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Court mode d'emploi : un tableau de déclinaison ne sert à rien si l'on ne sait
// pas qu'il s'agit d'un MODÈLE, valable pour des centaines d'autres mots.
const CoursTableaux = () => (
  <Lecon id="tableaux" titre="Comment se servir de ces tableaux">
    <p>
      Un mot russe ne s'apprend pas seul : il suit un <strong className="text-slate-200">modèle</strong>. Décliner
      дом, c'est savoir décliner les 1 408 masculins durs de la base. C'est pour ça
      qu'il n'y a ici qu'une douzaine de tableaux et non cinq mille.
    </p>
    <p className="text-slate-400">
      Le modèle se lit sur la finale du mot au nominatif : consonne pour un masculin
      dur, -а ou -я pour un féminin, -о ou -е pour un neutre, et -ь à part parce
      qu'il peut être masculin comme féminin. Chaque tableau est illustré par un mot
      que tu travailles déjà quand il en existe un.
    </p>
    <p className="text-slate-400">
      Pour les verbes, l'essentiel tient en une opposition : un
      <strong className="text-slate-200"> imperfectif</strong> a un présent et forme son futur avec буду ;
      un <strong className="text-slate-200">perfectif</strong> n'a pas de présent du tout, ses formes
      personnelles sont déjà un futur. Le tableau te le dit pour chaque verbe.
    </p>
    <p className="text-slate-500 text-[13px]">
      L'accent tonique n'est pas écrit, il s'entend : le haut-parleur est là pour ça.
      Il se déplace souvent d'une forme à l'autre, c'est normal et c'est justement
      ce qu'on ne peut pas deviner à l'écrit.
    </p>
  </Lecon>
);

export default function FormsTables({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('loading');
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('verbes');
  const [openId, setOpenId] = useState(null);
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  useEffect(() => {
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    fetch('/api/formes/tables?verbes=50', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { leave(); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => { if (d) { setData(d); setPhase('ready'); } })
      .catch(() => setPhase('error'));
  }, [token]);

  const connus = useMemo(
    () => (data ? data.verbes.filter((v) => v.connu).length : 0),
    [data],
  );

  if (phase === 'loading') {
    return (
      <Shell actif="/?tableaux" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell actif="/?tableaux" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-16">
          <p className="text-slate-400">Impossible de charger les tableaux.</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
            Réessayer
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell actif="/?tableaux" cursive={cursive} toggleCursive={toggleCursive}>
      <div className="flex flex-col gap-1">
        <h1 className="text-xl md:text-2xl font-bold">Tableaux</h1>
        <p className="text-sm text-slate-400">
          Les 50 verbes les plus fréquents et les modèles de déclinaison, illustrés par tes mots
          quand c'est possible. L'accent tonique n'est pas écrit, il s'entend : le haut-parleur
          est là pour ça.
        </p>
      </div>

      <CoursTableaux />

      <div className="flex gap-2">
        {[['verbes', `Conjugaison (${data.verbes.length})`], ['modeles', `Déclinaisons (${data.modeles.length})`]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              tab === k ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'verbes' ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-slate-500">
            Classés par fréquence. {connus} sont déjà dans ta progression.
          </p>
          {data.verbes.map((v) => (
            <VerbCard key={v.id} v={v} open={openId === v.id} onToggle={() => setOpenId(openId === v.id ? null : v.id)} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {data.modeles.map((m) => <ModelCard key={m.modele} m={m} />)}
        </div>
      )}

      <a href="/?formes" className="self-start text-sm text-blue-300 hover:text-blue-200 transition-colors">
        S'entraîner sur les formes
      </a>
    </Shell>
  );
}
